import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, Button, Typography, Paper, List, ListItem, ListItemButton, ListItemText, Divider, CircularProgress, IconButton, Alert } from '@mui/material';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { LocationOn, Search, Close, ErrorOutline } from '@mui/icons-material';
import { Service, PostcodeData, Location } from './types';
import { GOLDEN_MINOR } from '../../styles/golden';

// Mock data - In production, replace with actual API calls
const MOCK_SERVICES: Service[] = [
  {
    id: '1',
    name: 'Women\'s Aid',
    address: '123 Safety Street, London, E1 6AN',
    phone: '0808 2000 247',
    website: 'https://www.womensaid.org.uk',
    location: { latitude: 51.5208, longitude: -0.0799 },
    type: 'support',
    description: 'National domestic violence helpline'
  },
  {
    id: '2',
    name: 'Refuge',
    address: '2nd Floor, One America Square, London, EC3N 2SG',
    phone: '0808 2000 247',
    website: 'https://www.refuge.org.uk',
    location: { latitude: 51.5112, longitude: -0.0786 },
    type: 'refuge',
    description: 'Support for women and children against domestic violence'
  },
  // Add more mock services as needed
];

// The map fills its wrapper, which sets a Fibonacci height per screen size
const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '13px'
};

const mapHeight = { xs: 377, md: 610 };

const center = {
  lat: 51.509865,
  lng: -0.118092
};

const libraries: ('places')[] = ['places'];

// Search radius in meters (5km radius)
const SEARCH_RADIUS = 5000;

// Type for Google Places search result
interface PlaceResult extends google.maps.places.PlaceResult {
  types?: string[];
}

const ServiceLocator: React.FC = () => {
  const [postcode, setPostcode] = useState('');
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(center);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    
    if (service.location) {
      // Close any existing info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Create and open new info window
      const content = `
        <div style="padding: 8px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px;">${service.name}</h3>
          <p style="margin: 4px 0; font-size: 14px;">${service.address}</p>
          ${service.phone ? `<p style="margin: 4px 0; font-size: 14px;">Phone: ${service.phone}</p>` : ''}
          ${service.distance ? `<p style="margin: 4px 0; font-size: 14px;">Distance: ${service.distance.toFixed(1)} miles</p>` : ''}
        </div>
      `;
      
      infoWindowRef.current = new window.google.maps.InfoWindow({
        content,
        position: {
          lat: service.location.latitude,
          lng: service.location.longitude
        },
        maxWidth: 300
      });
      
      infoWindowRef.current.open(mapRef.current);
      
      setMapCenter({
        lat: service.location.latitude,
        lng: service.location.longitude
      });
    }
  };
  
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  // Search for domestic abuse services near coordinates using Google Places API
  const searchNearbyServices = useCallback(async (lat: number, lng: number) => {
    if (!window.google) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Create a PlacesService instance
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      
      // Search for domestic abuse support services
      const request = {
        location: new window.google.maps.LatLng(lat, lng),
        radius: SEARCH_RADIUS,
        query: 'domestic abuse support',
        types: ['health', 'point_of_interest', 'establishment']
      };
      
      // Execute the search
      const places = await new Promise<PlaceResult[]>((resolve, reject) => {
        service.textSearch(request, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results as PlaceResult[]);
          } else {
            console.error('Places API error:', status);
            reject(new Error('Failed to fetch places'));
          }
        });
      });
      
      // Format the results
      const nearbyServices = places.map((place, index) => ({
        id: place.place_id || `place-${index}`,
        name: place.name || 'Domestic Abuse Support',
        address: place.formatted_address || '',
        phone: place.formatted_phone_number || 'Contact for details',
        website: place.website?.toString() || '',
        location: {
          latitude: place.geometry?.location?.lat() || lat,
          longitude: place.geometry?.location?.lng() || lng
        },
        type: 'support' as const,  // Ensure type is one of the allowed values
        description: place.vicinity || '',
        rating: place.rating,
        user_ratings_total: place.user_ratings_total,
        distance: calculateDistance(
          lat,
          lng,
          place.geometry?.location?.lat() || lat,
          place.geometry?.location?.lng() || lng
        )
      }));
      
      // Sort by distance and filter out any invalid entries
      const validServices = nearbyServices.filter(service => 
        service.location.latitude && service.location.longitude
      ).sort((a, b) => (a.distance || 0) - (b.distance || 0));
      
      setServices(validServices);
      
      // Pan to the first result if available
      if (validServices.length > 0 && mapRef.current) {
        mapRef.current.panTo({
          lat: validServices[0].location.latitude,
          lng: validServices[0].location.longitude
        });
      } else {
        setError('No domestic abuse support services found in your area. Please try a different location.');
      }
      
    } catch (err) {
      console.error('Error searching services:', err);
      setError('Failed to load nearby services. Please try again or check your internet connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Try to get user's current location on component mount
  useEffect(() => {
    if (isLoaded && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newCenter = { lat: latitude, lng: longitude };
          setMapCenter(newCenter);
          setLocationError(null);
          
          // Automatically search for nearby services
          await searchNearbyServices(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to retrieve your location. Please enter a postcode instead.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000, // 10 seconds
          maximumAge: 0 // Force fresh location
        }
      );
    }
  }, [isLoaded, searchNearbyServices]);

  const handleSearch = async () => {
    const postcodeToSearch = postcode.trim().replace(/\s+/g, '').toUpperCase();
    
    if (!postcodeToSearch) {
      setError('Please enter a postcode');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use Postcodes.io API to get coordinates for the postcode
      const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcodeToSearch)}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to find postcode');
      }
      
      const data = await response.json();
      
      if (!data.result) {
        throw new Error('No results found for this postcode');
      }
      
      const postcodeData: PostcodeData = {
        postcode: data.result.postcode,
        longitude: data.result.longitude,
        latitude: data.result.latitude,
        admin_district: data.result.admin_district || data.result.region || 'Unknown area',
        region: data.result.region || data.result.admin_district || 'Unknown region',
        country: data.result.country || 'UK'
      };
      
      const newCenter = {
        lat: postcodeData.latitude,
        lng: postcodeData.longitude
      };
      
      setMapCenter(newCenter);
      
      // Search for services near the postcode
      await searchNearbyServices(postcodeData.latitude, postcodeData.longitude);
    } catch (err) {
      console.error('Error fetching postcode data:', err);
      setError('Could not find this postcode. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    // Haversine formula to calculate distance between two points
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  const handleClear = () => {
    setPostcode('');
    setMapCenter(center);
    setServices([]);
    setError(null);
  };


  const renderMap = () => {
    if (loadError) {
      return (
        <Alert severity="error" icon={<ErrorOutline />}>
          Error loading Google Maps. Please check your internet connection and try again.
        </Alert>
      );
    }

    if (!isLoaded) {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={12}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      >
        <Marker
          position={mapCenter}
          icon={{
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          }}
        />
        
        {services.map((service) => (
          <Marker
            key={service.id}
            position={{
              lat: service.location.latitude,
              lng: service.location.longitude
            }}
            onClick={() => handleServiceSelect(service)}
          />
        ))}
      </GoogleMap>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Find nearest support center
      </Typography>
      
      {locationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {locationError}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter your postcode (e.g., E1 6AN)"
          inputProps={{ 'aria-label': 'Postcode', autoCapitalize: 'characters' }}
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          disabled={isLoading}
          InputProps={{
            startAdornment: <LocationOn color="action" sx={{ mr: 1 }} />
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={isLoading || !postcode.trim()}
          startIcon={<Search />}
          sx={{ flexShrink: 0, px: 4 }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
        {(postcode || services.length > 0) && (
          <Button onClick={handleClear} startIcon={<Close />}>
            Clear
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Results and map split 38.2% / 61.8% (golden ratio) side by side on desktop */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Services List */}
        {services.length > 0 && (
          <Paper sx={{ width: { xs: '100%', md: GOLDEN_MINOR }, flexShrink: 0, maxHeight: mapHeight, overflow: 'auto', borderRadius: '13px' }}>
            <List>
              {services.map((service, index) => (
                <React.Fragment key={service.id}>
                  <ListItem 
                    disablePadding
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleServiceSelect(service)}>
                        <LocationOn />
                      </IconButton>
                    }
                  >
                    <ListItemButton 
                      selected={selectedService?.id === service.id}
                      onClick={() => handleServiceSelect(service)}
                    >
                    <ListItemText
                      primary={service.name}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary" display="block">
                            {service.address}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {service.distance?.toFixed(1)} km away • {service.phone}
                          </Typography>
                        </>
                      }
                    />
                    </ListItemButton>
                  </ListItem>
                  {index < services.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {/* Map */}
        <Box sx={{ flex: { md: 1 }, width: '100%', minWidth: 0, height: mapHeight, position: 'relative' }}>
          {renderMap()}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceLocator;
